/**
 * Calculateur de taille NAKED : cœur fiche (asset « nkd-taille.js »).
 * Lit la fiche, garde la mémoire, applique une taille en la vérifiant, pré-coche au chargement,
 * informe la carte d'entrée, ouvre la popup, mesure. Ne calcule rien : le calcul est dans NakedSizeEngine.
 * N'ajoute jamais au panier, n'écrit jamais input[name=id] : il clique sur les pastilles, le thème fait le reste.
 *
 * API publique : window.NakedSize
 *   .context()                  → { inScope, country, lang, system, runsSmall, product: { kind, sizes, runsSmall, isBody },
 *                                   axes, picker, form, idInput, bundle, data } ou null (pas de racine [data-nkd-taille]).
 *                                 Relu à chaque appel. inScope = 1 ou 2 axes de taille S/M/L/XL, tous lisibles.
 *                                 isBody = la racine porte data-body="1" (titre du produit contenant « body »).
 *                                 Axe de taille : toutes ses valeurs sont S/M/L/XL. Une seule valeur inconnue (valeur mal
 *                                 traduite dans le catalogue : « L » devenu « The ») est tolérée sur un axe d'au moins
 *                                 3 valeurs si la taille manquante se déduit sans ambiguïté (voir readSizes) : axes[i].sizes
 *                                 porte alors la taille déduite, le bouton radio garde sa valeur réelle. Sinon : hors champ.
 *   .recommend(answers, pref)   → conseil pour CETTE fiche, ou null (hors champ, réponses illisibles) :
 *                                 { rec (résultat du moteur), kind, level: 'ok' | 'closest' | 'none',
 *                                   shown: { top, bottom } | { single }  (tailles à montrer),
 *                                   sizes: idem, seulement si level 'ok' (tailles à appliquer),
 *                                   variant: { id, available } | null,
 *                                   status: 'taille' | 'epuisee' | 'inexistante' | 'non-conseillee' | 'aucune' }
 *   .variantFor(sizes)          → { id, available, options } | null (couleur courante gardée)
 *   .apply(sizes, origin)       → Promise<{ ok, reason, variantId }>. sizes : { top, bottom } ou { single }.
 *                                 origin : 'bouton' (défaut) | 'memoire' | 'offre'. Jamais de taille de remplacement.
 *                                 Raisons d'échec : pas-de-selecteur, selecteur-pas-pret, taille-manquante, inexistante,
 *                                 epuisee, taille-absente-unite, deja-choisi, pastille-non-cochee, variante-non-suivie,
 *                                 garde-fou-actif, erreur.
 *   .memory.load() / .save(answers, pref) / .clear()
 *                                 localStorage.naked_size_data = { v: 2, topSize, bottomSizeValue, bodySize, answers,
 *                                 pref, tableVersion, lastUpdated }. Toute donnée sans v: 2 ou de plus de 13 mois est purgée.
 *   .refresh()                  → recalcule l'état de la carte depuis la mémoire (à appeler après memory.save) ;
 *                                 remet le bouton panier en attente de taille si le conseil n'est pas applicable.
 *   .open({ source })           → Promise<bool>. Charge la popup au premier besoin puis NakedSizePopup.open(ctx).
 *   .track(name, params)        → évènement DOM « nkd:taille:mesure » + gtag vers la seule destination data-ga4.
 *   .cardState                  → dernier détail de « nkd:taille:etat » (pour un script arrivé après l'évènement).
 *
 * Évènement « nkd:taille:etat » (sur document), detail :
 *   { etat: 'vide' | 'taille' | 'non-conseillee' | 'aucune' | 'epuisee', texte, action, aria,
 *     tailles: { haut, bas, unique } | null, applique: bool, raison }
 *
 * Configuration de marque (facultative, lue sur la racine [data-nkd-taille] ; sans ces attributs : fiche NAKED) :
 *   data-marque      nom court posé sur la popup (dialog.nsz[data-marque]) pour la feuille de variables de la marque
 *   data-marque-css  URL de cette feuille, chargée après celle de la popup
 *   data-cle         clé localStorage de la mémoire
 *   data-ouvrir      sélecteur des déclencheurs d'ouverture
 *   data-sceau       lignes du sceau de preuve sociale, séparées par « | »
 *   data-fiche       JSON, structure de la fiche : voir FICHE ci-dessous
 *
 * Carte écrite par le cœur (gabarit sans carte nkd-size-ui, ou marque sœur) : le texte d'état remplace le contenu de
 * l'élément ; à l'état « vide » (mémoire effacée) le contenu d'origine est rendu. Le déclencheur porte l'état courant
 * dans data-nkd-taille-etat (« vide », « taille », …).
 *
 * Page d'atterrissage (facultatif) : la racine porte data-landing (JSON, voir LANDING ci-dessous). La page a son propre
 * formulaire d'achat : le cœur n'y cherche ni .nk-pdp-info ni .nk-picker. Il lit les champs d'option du formulaire
 * (listes <select> ou groupes de boutons radio, dans l'ordre des options du produit), règle la taille dans ces champs,
 * émet leurs évènements « input » et « change » (le script de la page recalcule sa variante), puis VÉRIFIE que le champ
 * de variante du formulaire porte la variante voulue. Même mémoire, même popup, même moteur que sur une fiche.
 * Texte d'état (« Votre taille : haut M · bas L · Modifier ») : dans les boutons créés par le cœur et dans les
 * déclencheurs de la page (ouvrir) situés dans un formulaire d'achat ; les autres déclencheurs gardent leur texte.
 * Raisons d'échec propres à ce mode : champ-non-regle. Sans data-landing, rien de ce mode ne s'exécute.
 */
(function (w) {
  'use strict';
  if (!w || !w.document || (w.NakedSize && w.NakedSize.__core)) return; // inclus deux fois : sans effet
  var d = w.document;

  var KEY = 'naked_size_data'; // défaut ; une marque sœur donne la sienne par data-cle
  var MAX_AGE = 395 * 864e5; // 13 mois
  var RANK = { S: 1, M: 2, L: 3, XL: 4 };
  var SEL_OPEN = '[data-nkd-sizecalc], .naked-size-inline-trigger, #naked-size-button';
  var SEL_ATC = '.nk-buy button[type="submit"], .product-quick-add button[type="submit"]';
  var HOLD = 'data-nkd-taille-attente';
  var ORIGIN = 'data-nkd-taille-origine'; // contenu d'origine d'un élément de la carte, rendu à l'état « vide »
  var ARIA0 = 'data-nkd-taille-aria';     // aria-label d'origine du déclencheur
  var ETAT = 'data-nkd-taille-etat';      // état courant, posé sur la carte (la feuille du thème peut s'y accrocher)
  var DELAYS = [0, 150, 400, 900, 1500];

  // Structure de la fiche : valeurs par défaut = thème NAKED. Une marque sœur les surcharge par l'attribut data-fiche (JSON).
  var FICHE = {
    axes: 'fieldset.nk-opt',   // groupes d'options du sélecteur de variantes
    atc: SEL_ATC,                              // boutons panier mis en attente de taille
    attente: 'awaiting-size',                  // classe « en attente de taille » de ces boutons
    garde: '.nk-buy button[type="submit"].awaiting-size', // un élément trouvé = le garde-fou retient encore
    gardePret: '',                             // signal « le garde-fou a joué » : « sélecteur@propriété » ; vide = window.__nkSizeSets
    offres: '[data-njb]',                      // racine des offres groupées
    offresUnite: 'data-njb-unit',              // attribut des boutons d'unité (l'unité active porte .is-on)
    offresChoix: '[data-njb-offer], [data-njb-unit]', // un clic ici = changement d'offre
    offresPret: '',                            // sélecteur présent quand le script d'offres a fini ; vide = picker.dataset.njbTag
    offresTaille: true,                        // le script d'offres coche une taille d'office
    carte: 'data-nkd-sizecalc',                // attribut de la carte d'entrée (source « carte » dans la mesure)
    carteTexte: '',                            // élément qui reçoit le texte d'état ; vide = lien .naked-size-inline-trigger des gabarits sans carte
    carteAction: '',                           // élément qui reçoit l'action (« Modifier ») ; vide = à la suite du texte
    carteSous: ''                              // sous-titre de la carte écrit par le thème pour l'ancien calculateur (« 3 questions ») :
                                               // remplacé au chargement par le texte card_sub ; vide = rien à corriger
  };
  // Page d'atterrissage : valeurs par défaut, surchargées par l'attribut data-landing (JSON) de la racine.
  var LANDING = {
    formulaire: 'form',        // formulaire(s) d'achat de la page ; plusieurs formulaires tenus en phase par la page : le premier visible
    champs: 'select',          // champs d'option dans le formulaire, dans l'ordre des options du produit : <select>, ou groupe de boutons radio
    id: 'input[name="id"]',    // champ de la variante, tenu par le script de la page : c'est lui qui est vérifié
    ouvrir: '',                // déclencheurs déjà présents dans la page : leur clic est repris, les autres écouteurs ne le voient pas
    boutonAvant: '',           // aucun déclencheur présent : un bouton est créé dans chaque formulaire, devant le premier élément trouvé
    boutonClasse: '',          // classe du bouton créé (style de la page)
    memoire: true              // au chargement, régler les champs encore vides depuis la mémoire
  };
  var TRIGGER = 'data-nkd-taille-ouvrir'; // bouton créé par le cœur sur une page d'atterrissage
  var cache = { raw: null, fiche: FICHE };
  var cacheL = { raw: null, conf: null };
  function landing() { // null hors page d'atterrissage
    var r = root0();
    if (!r || !r.hasAttribute('data-landing')) return null;
    var raw = r.getAttribute('data-landing') || '';
    if (raw === cacheL.raw) return cacheL.conf;
    var out = {}, over = null, k;
    try { over = raw ? JSON.parse(raw) : null; } catch (e) {}
    for (k in LANDING) out[k] = over && Object.prototype.hasOwnProperty.call(over, k) && typeof over[k] === typeof LANDING[k] ? over[k] : LANDING[k];
    cacheL = { raw: raw, conf: out };
    return out;
  }
  function openSel() {
    var L = landing();
    return L ? (L.ouvrir ? L.ouvrir + ', ' : '') + '[' + TRIGGER + ']' : (attr('data-ouvrir') || SEL_OPEN);
  }
  function root0() { return d.querySelector('[data-nkd-taille]'); }
  function attr(name) { var r = root0(); return (r && r.getAttribute(name)) || ''; }
  function fiche() {
    var raw = attr('data-fiche');
    if (raw === cache.raw) return cache.fiche;
    var out = {}, over = null, k;
    try { over = raw ? JSON.parse(raw) : null; } catch (e) {}
    for (k in FICHE) out[k] = over && Object.prototype.hasOwnProperty.call(over, k) && typeof over[k] === typeof FICHE[k] ? over[k] : FICHE[k];
    cache = { raw: raw, fiche: out };
    return out;
  }
  function key() { return attr('data-cle') || KEY; }
  function signal(spec) { // « sélecteur@propriété » : l'élément existe et sa propriété est posée (liste : non vide)
    try {
      var p = String(spec).split('@'), el = d.querySelector(p[0]), v = el ? (p[1] ? el[p[1]] : true) : null;
      return !!v && (typeof v !== 'object' || v.length === undefined || v.length > 0);
    } catch (e) { return false; }
  }
  function guardReady() { var g = fiche().gardePret; return g ? signal(g) : !!w.__nkSizeSets; }

  // Textes de la carte : pris d'abord dans nkd-taille-textes.js (window.NakedSizeTexts) ; repli ci-dessous si ce
  // fichier n'a pas chargé. Le repli est la copie des clés card_* des 8 langues.
  /* CARTE:DEBUT (généré par tests/build-template.mjs à partir de src/texts-<langue>.json : ne pas modifier à la main) */
  var CARD = {
    fr: { card_size_set: "Votre taille : haut {top} · bas {bottom}", card_size_single: "Votre taille : {size}", card_soldout: "Votre taille {size} est épuisée", card_not_advised: "Taille à vérifier pour ce modèle", card_out: "Pas de taille conseillée pour ce modèle", card_edit: "Modifier", card_sub: "Répondez à quelques questions et obtenez votre taille idéale." },
    en: { card_size_set: "Your size: top {top} · bottom {bottom}", card_size_single: "Your size: {size}", card_soldout: "Your size {size} is sold out", card_not_advised: "Double-check the size for this style", card_out: "No recommended size for this style", card_edit: "Edit", card_sub: "Answer a few questions and get your ideal size." },
    de: { card_size_set: "Ihre Größe: oben {top} · unten {bottom}", card_size_single: "Ihre Größe: {size}", card_soldout: "Ihre Größe {size} ist ausverkauft", card_not_advised: "Größe bei diesem Modell prüfen", card_out: "Keine Größenempfehlung für dieses Modell", card_edit: "Ändern", card_sub: "Beantworten Sie ein paar Fragen und erhalten Sie Ihre ideale Größe." },
    nl: { card_size_set: "Jouw maat: top {top} · broekje {bottom}", card_size_single: "Jouw maat: {size}", card_soldout: "Jouw maat {size} is uitverkocht", card_not_advised: "Check je maat voor dit model", card_out: "Geen maatadvies voor dit model", card_edit: "Wijzigen", card_sub: "Beantwoord een paar vragen en ontdek je ideale maat." },
    it: { card_size_set: "La tua taglia: sopra {top} · sotto {bottom}", card_size_single: "La tua taglia: {size}", card_soldout: "La tua taglia {size} è esaurita", card_not_advised: "Taglia da verificare per questo modello", card_out: "Nessuna taglia consigliata per il modello", card_edit: "Modifica", card_sub: "Rispondi a qualche domanda e scopri la tua taglia ideale." },
    pl: { card_size_set: "Twój rozmiar: góra {top} · dół {bottom}", card_size_single: "Twój rozmiar: {size}", card_soldout: "Twój rozmiar {size} jest wyprzedany", card_not_advised: "Sprawdź rozmiar dla tego modelu", card_out: "Brak polecanego rozmiaru dla tego modelu", card_edit: "Zmień", card_sub: "Odpowiedz na kilka pytań i poznaj swój idealny rozmiar." },
    da: { card_size_set: "Din størrelse: overdel {top} · underdel {bottom}", card_size_single: "Din størrelse: {size}", card_soldout: "Din størrelse {size} er udsolgt", card_not_advised: "Tjek størrelsen på denne model", card_out: "Ingen anbefalet størrelse til denne model", card_edit: "Ret", card_sub: "Svar på et par spørgsmål, og find din ideelle størrelse." },
    nb: { card_size_set: "Din størrelse: overdel {top} · underdel {bottom}", card_size_single: "Din størrelse: {size}", card_soldout: "Din størrelse {size} er utsolgt", card_not_advised: "Sjekk størrelsen på denne modellen", card_out: "Ingen anbefalt størrelse for denne modellen", card_edit: "Endre", card_sub: "Svar på noen få spørsmål, så finner vi den perfekte størrelsen din." },
    fi: { card_size_set: "Kokosi: yläosa {top} · alaosa {bottom}", card_size_single: "Kokosi: {size}", card_soldout: "Kokosi {size} on loppuunmyyty", card_not_advised: "Tarkista koko tässä mallissa", card_out: "Tähän malliin ei ole kokosuositusta", card_edit: "Muokkaa", card_sub: "Vastaa muutamaan kysymykseen, niin löydät täydellisen kokosi." },
    sv: { card_size_set: "Din storlek: överdel {top} · underdel {bottom}", card_size_single: "Din storlek: {size}", card_soldout: "Din storlek {size} är slutsåld", card_not_advised: "Kolla storleken på den här modellen", card_out: "Ingen rekommenderad storlek för modellen", card_edit: "Ändra", card_sub: "Svara på några frågor och hitta din perfekta storlek." }/* nk-propre */,
    es: { card_size_set: "Tu talla: arriba {top} · abajo {bottom}", card_size_single: "Tu talla: {size}", card_soldout: "Tu talla {size} está agotada", card_not_advised: "Revisa la talla para este modelo", card_out: "No hay talla recomendada para este modelo", card_edit: "Editar", card_sub: "Responde unas preguntas y descubre tu talla ideal." }/* /nk-propre */
  };
  /* CARTE:FIN */

  // État de page (jamais d'élément du DOM gardé d'un appel à l'autre).
  var state = { sizes: null, applied: false, userChose: false, hold: null, openedAt: 0, opening: false, popup: null, form: -1 };

  // ---------- outils ----------
  function safe(fn) {
    return function () {
      try { return fn.apply(this, arguments); } catch (e) { try { track('error', { where: fn.name || '' }); } catch (e2) {} }
    };
  }
  function arr(list) { return Array.prototype.slice.call(list || []); }
  function norm(v) { return String(v == null ? '' : v).trim().toUpperCase(); }
  function closest(el, sel) { return el && el.closest ? el.closest(sel) : null; }
  function pause(ms) { return new Promise(function (resolve) { w.setTimeout(resolve, ms); }); }
  function waitFor(test, tries, step) {
    return new Promise(function (resolve) {
      (function loop(n) {
        var ok = false;
        try { ok = !!test(); } catch (e) {}
        if (ok || n <= 0) return resolve(ok);
        w.setTimeout(function () { loop(n - 1); }, step);
      })(tries);
    });
  }
  function visible(el) {
    try {
      if (!el) return false;
      if (typeof w.getComputedStyle === 'function' && w.getComputedStyle(el).display === 'none') return false;
      return el.offsetParent !== null;
    } catch (e) { return true; }
  }
  // Tailles d'un axe, dans l'ordre de ses valeurs. false : pas un axe de taille (couleur, « S-M »). null : axe de taille
  // illisible (fiche hors champ). Une valeur inconnue est tolérée s'il y a au moins 3 valeurs, que les autres sont dans
  // l'ordre S, M, L, XL et que la taille voisine manquante est unique : [S, M, The] → L ; [K, M, L] → S ; [S, The, XL] → null.
  // Même règle, côté serveur, dans le snippet nkd-taille.liquid : les deux doivent rester identiques.
  function readSizes(values) {
    var ranks = values.map(function (v) { return RANK[norm(v)] || 0; });
    var known = ranks.filter(function (r) { return r; }), bad = ranks.length - known.length;
    if (!known.length) return false;
    if (!bad) return values.map(norm);
    var i = ranks.indexOf(0), prev = i > 0 ? ranks[i - 1] : 0, next = i < ranks.length - 1 ? ranks[i + 1] : 0;
    var cand = prev ? prev + 1 : next - 1;
    var ordered = known.every(function (r, k) { return !k || r > known[k - 1]; });
    if (bad > 1 || ranks.length < 3 || !ordered || cand < 1 || cand > 4 || (prev && next && next - 1 !== cand)) return null;
    ranks[i] = cand;
    var names = Object.keys(RANK);
    return ranks.map(function (r) { return names[r - 1]; });
  }
  // Taille portée par une valeur d'option sur un axe : la taille déduite si la valeur est mal traduite.
  function sizeOn(axis, value) {
    var n = norm(value);
    for (var i = 0; i < axis.radios.length; i++) if (norm(axis.radios[i].value) === n) return axis.sizes[i];
    return RANK[n] ? n : '';
  }
  function fail(reason, variantId) { return { ok: false, reason: reason, variantId: variantId == null ? null : variantId }; }
  function engine() { return w.NakedSizeEngine || null; }

  // ---------- lecture d'une page d'atterrissage ----------
  // Champs d'option d'un formulaire, dans l'ordre : { el, select (ou null), inputs (options ou radios), position }.
  function fieldsOf(form, L) {
    return arr(form.querySelectorAll(L.champs)).map(function (el, i) {
      var select = String(el.tagName).toUpperCase() === 'SELECT' ? el : null;
      var inputs = select ? arr(el.querySelectorAll('option')).filter(function (o) { return o.value !== ''; })
        : arr(el.querySelectorAll('input[type="radio"]'));
      return { el: el, select: select, inputs: inputs, position: i + 1 };
    }).filter(function (f) { return f.inputs.length; });
  }
  function landingForms(L) {
    return arr(d.querySelectorAll(L.formulaire)).filter(function (f) { return fieldsOf(f, L).length > 0; });
  }
  // Valeur courante d'un champ ('' : rien de choisi).
  function valueOf(f) {
    if (f.select) return String(f.select.value || '');
    var on = (f.radios || f.inputs).filter(function (r) { return r.checked; })[0];
    return on ? String(on.value) : '';
  }
  function readLanding(L, data) {
    var forms = landingForms(L);
    // formulaire du dernier déclencheur utilisé, sinon le premier visible, sinon le premier
    var form = forms[state.form] || forms.filter(visible)[0] || forms[0] || null;
    var all = form ? fieldsOf(form, L) : [], axes = [], unreadable = false;
    all.forEach(function (f) {
      var sizes = readSizes(f.inputs.map(function (o) { return o.value; }));
      if (sizes === null) unreadable = true;
      if (sizes) axes.push({ fs: f.el, select: f.select, radios: f.inputs, position: f.position, sizes: sizes, visible: true });
    });
    var idInput = form ? form.querySelector(L.id) : null;
    var first = data && data.variantes && data.variantes[0];
    // chaque option du produit doit avoir son champ : sinon la variante du formulaire ne peut pas être désignée
    var complete = !!first && arr(first[1]).length === all.length;
    return { form: form, idInput: idInput, axes: axes, fields: all, ok: !unreadable && !!idInput && complete };
  }

  // ---------- lecture de la fiche ----------
  function context() {
    var root = d.querySelector('[data-nkd-taille]'); // rendue deux fois : la première fait foi
    if (!root) return null;
    var F = fiche(), L = landing();
    var data = null;
    try {
      var island = root.querySelector('[data-nkd-taille-produit]');
      data = island ? JSON.parse(island.textContent || 'null') : null;
    } catch (e) {}
    var info = L ? null : d.querySelector('.nk-pdp-info');
    var picker = info ? info.querySelector('.nk-picker') : null;
    var formId = picker ? picker.getAttribute('form') : null;
    var form = formId ? d.getElementById(formId) : null;
    var axes = [], unreadable = false, page = null;
    if (picker) arr(picker.querySelectorAll(F.axes)).forEach(function (fs) {
      var radios = arr(fs.querySelectorAll('input[type="radio"]')).filter(function (r) { return /^option\d+$/.test(r.name || ''); });
      var sizes = radios.length ? readSizes(radios.map(function (r) { return r.value; })) : false;
      if (sizes === null) unreadable = true;
      if (!sizes) return;
      axes.push({ fs: fs, radios: radios, position: parseInt(radios[0].name.slice(6), 10), sizes: sizes, visible: visible(fs) });
    });
    if (L) { page = readLanding(L, data); form = page.form; axes = page.axes; unreadable = !page.ok; }
    var inScope = !unreadable && (axes.length === 1 || axes.length === 2);
    var sizes = Object.keys(RANK).filter(function (s) { return axes.some(function (a) { return a.sizes.indexOf(s) >= 0; }); });
    var country = root.getAttribute('data-pays') || (w.Shopify && w.Shopify.country) || '';
    var lang = root.getAttribute('data-langue') || '';
    var runsSmall = root.getAttribute('data-coupe') === 'petit';
    var E = engine();
    return {
      root: root, data: data, picker: picker, form: form,
      idInput: page ? page.idInput : (form ? form.querySelector('input[name="id"]') : null),
      axes: axes, inScope: inScope, bundle: L ? null : d.querySelector(F.offres),
      landing: L, fields: page ? page.fields : null,
      country: country, lang: lang, system: E ? E.systemFor(country, lang) : 'FR', runsSmall: runsSmall,
      product: inScope ? { kind: axes.length === 2 ? 'set' : 'single', sizes: axes.length === 2 ? axes[0].sizes : sizes, bottomSizes: axes.length === 2 ? axes[1].sizes : null, runsSmall: runsSmall, isBody: root.getAttribute('data-body') === '1' } : null
    };
  }

  // Variantes de l'îlot ; la disponibilité du thème (picker.product), plus fraîche, prend le pas.
  function variants(ctx) {
    var fresh = {};
    try {
      arr(ctx.picker && ctx.picker.product && ctx.picker.product.variants).forEach(function (v) { fresh[String(v.id)] = !!v.available; });
    } catch (e) {}
    return arr(ctx.data && ctx.data.variantes).map(function (v) {
      var id = String(v[0]);
      return { id: id, options: arr(v[1]), available: fresh.hasOwnProperty(id) ? fresh[id] : !!v[2] };
    });
  }

  function targetsFor(ctx, sizes) {
    sizes = sizes || {};
    return ctx.axes.map(function (a, i) {
      return { axis: a, size: norm(ctx.axes.length === 1 ? sizes.single : (i === 0 ? sizes.top : sizes.bottom)) };
    });
  }

  // La variante voulue : tailles demandées sur les axes de taille, autres options (couleur) = celles de la fiche.
  function pickVariant(ctx, targets) {
    var list = variants(ctx);
    if (!list.length || targets.some(function (t) { return !RANK[t.size]; })) return null;
    var want = {}, axisAt = {};
    targets.forEach(function (t) { want[t.axis.position] = t.size; axisAt[t.axis.position] = t.axis; });
    var cur = ctx.idInput ? list.filter(function (v) { return v.id === String(ctx.idInput.value); })[0] : null;
    var ref = (cur || list[0]).options.slice();
    if (ctx.picker) arr(ctx.picker.querySelectorAll('input[type="radio"]')).forEach(function (r) {
      var m = /^option(\d+)$/.exec(r.name || '');
      if (m && r.checked && !want[m[1]]) ref[m[1] - 1] = r.value;
    });
    if (ctx.fields) ctx.fields.forEach(function (f) { if (!want[f.position]) ref[f.position - 1] = valueOf(f); });
    for (var i = 0; i < list.length; i++) {
      var o = list[i].options, ok = true;
      for (var p = 0; p < o.length && ok; p++) ok = want[p + 1] ? sizeOn(axisAt[p + 1], o[p]) === want[p + 1] : String(o[p]) === String(ref[p]);
      if (ok) return list[i];
    }
    return null;
  }

  function variantFor(sizes) {
    var ctx = context();
    if (!ctx || !ctx.inScope) return null;
    var v = pickVariant(ctx, targetsFor(ctx, sizes));
    return v ? { id: v.id, available: v.available, options: v.options } : null;
  }

  // ---------- conseil pour cette fiche ----------
  function advise(answers, pref, ctx) {
    ctx = ctx || context();
    var E = engine();
    if (!E || !ctx || !ctx.inScope || !answers) return null;
    var rec = E.recommend(answers, pref || null, ctx.product);
    if (!rec) return null;
    var parts = rec.kind === 'set' ? [rec.top, rec.bottom] : [rec.single];
    var level = parts.some(function (p) { return p.level === 'none' || !p.size; }) ? 'none'
      : parts.some(function (p) { return p.level !== 'ok'; }) ? 'closest' : 'ok';
    var shown = rec.kind === 'set' ? { top: rec.top.size, bottom: rec.bottom.size } : { single: rec.single.size };
    var out = { rec: rec, kind: rec.kind, level: level, shown: shown, sizes: null, variant: null, status: level === 'none' ? 'aucune' : 'non-conseillee' };
    if (level === 'ok') {
      var v = pickVariant(ctx, targetsFor(ctx, shown));
      out.sizes = shown;
      out.variant = v ? { id: v.id, available: v.available } : null;
      out.status = !v ? 'inexistante' : (v.available ? 'taille' : 'epuisee');
    }
    return out;
  }

  // ---------- mémoire v2 ----------
  function store() { return w.localStorage; } // peut jeter (navigation privée) : toujours appelé dans un try
  var memory = {
    load: function () {
      try {
        var raw = store().getItem(key());
        if (!raw) return null;
        var m = null;
        try { m = JSON.parse(raw); } catch (e) {}
        var age = m ? Date.now() - Date.parse(m.lastUpdated) : NaN;
        if (!m || m.v !== 2 || !m.answers || !(age < MAX_AGE)) { store().removeItem(key()); return null; }
        return m;
      } catch (e) { return null; }
    },
    save: function (answers, pref) {
      try {
        var E = engine(), g = E && E.generic(answers, pref || null);
        if (!g) return null;
        var m = {
          v: 2, topSize: g.topSize, bottomSizeValue: g.bottomSizeValue, bodySize: g.bodySize,
          answers: answers, pref: pref || null, tableVersion: E.TABLE_VERSION, lastUpdated: new Date().toISOString()
        };
        store().setItem(key(), JSON.stringify(m));
        return m;
      } catch (e) { return null; }
    },
    clear: function () { try { store().removeItem(key()); return true; } catch (e) { return false; } }
  };

  // ---------- mesure ----------
  function track(name, params) {
    params = params || {};
    try { d.dispatchEvent(new w.CustomEvent('nkd:taille:mesure', { detail: { name: name, params: params } })); } catch (e) {}
    try {
      var root = d.querySelector('[data-nkd-taille]'), dest = root && root.getAttribute('data-ga4');
      if (dest && typeof w.gtag === 'function') {
        var p = { send_to: dest }; // sans send_to, l'évènement partirait aussi vers Google Ads
        for (var k in params) if (Object.prototype.hasOwnProperty.call(params, k)) p[k] = params[k];
        w.gtag('event', 'nsr_' + name, p);
      }
    } catch (e) {}
  }

  // ---------- carte d'entrée ----------
  function text(lang, key, vars) {
    var code = String(lang || 'fr').toLowerCase(), l = code.slice(0, 2), T = w.NakedSizeTexts, s = '';
    try { // d'abord le fichier de textes : langue longue (de-at), courte (de), puis français
      var tables = T ? [T[code], T[l], T.fr] : [];
      for (var i = 0; i < tables.length && !s; i++) if (tables[i] && typeof tables[i][key] === 'string') s = tables[i][key];
    } catch (e) { s = ''; }
    if (!s) s = (CARD[l] || CARD.fr)[key] || CARD.fr[key] || '';
    return String(s).replace(/\{(\w+)\}/g, function (m, k) { return vars && vars[k] != null ? vars[k] : ''; });
  }
  // Écrit dans un élément de la carte en gardant son contenu d'origine (texte et balises du thème) dans un attribut,
  // pour le rendre tel quel à l'état « vide » (après « Effacer »). L'élément est relu à chaque appel : si le thème le
  // rend à nouveau, l'attribut a disparu avec lui et le nouveau contenu d'origine est repris.
  function write(el, value) {
    if (!el) return;
    if (!el.hasAttribute(ORIGIN)) el.setAttribute(ORIGIN, el.innerHTML);
    el.textContent = value;
  }
  function restore(el) {
    if (!el || !el.hasAttribute(ORIGIN)) return;
    el.innerHTML = el.getAttribute(ORIGIN);
    el.removeAttribute(ORIGIN);
  }
  function publish(detail) {
    api.cardState = detail;
    try { d.dispatchEvent(new w.CustomEvent('nkd:taille:etat', { detail: detail })); } catch (e) {}
    try { // gabarits sans carte : le petit lien « Calculer ma taille » porte le même texte
      var F = fiche(), info = d.querySelector('.nk-pdp-info');
      if (landing()) {
        // page d'atterrissage : le texte d'état va dans les boutons créés par le cœur et dans les déclencheurs de la
        // page (L.ouvrir) placés DANS un formulaire d'achat (« Size help » à côté des tailles) ; ceux d'ailleurs
        // (question fréquente, fenêtre d'aide) gardent leur texte. Contenu d'origine rendu à l'état « vide ».
        var L = landing(), shown = detail.etat !== 'vide' && !!detail.texte;
        var forms = L.ouvrir ? landingForms(L) : [];
        arr(d.querySelectorAll('[' + TRIGGER + ']' + (L.ouvrir ? ', ' + L.ouvrir : ''))).forEach(function (b) {
          if (!b.hasAttribute(TRIGGER) && forms.indexOf(closest(b, 'form')) < 0) return;
          if (shown) write(b, detail.texte + (detail.action ? ' · ' + detail.action : '')); else restore(b);
          b.setAttribute(ETAT, shown ? detail.etat : 'vide');
        });
        return;
      }
      var link = F.carteTexte ? d.querySelector(F.carteTexte)
        : info && !info.querySelector('[data-nkd-sizecalc]') ? info.querySelector('.naked-size-inline-trigger .link') : null;
      var act = link && F.carteAction ? d.querySelector(F.carteAction) : null;
      var sous = F.carteSous ? d.querySelector(F.carteSous) : null;
      if (sous && !sous.hasAttribute(ORIGIN)) { // une fois par rendu, avant toute écriture : c'est ce texte qui sera rendu à l'état « vide »
        var st = text(attr('data-langue'), 'card_sub');
        if (st) sous.textContent = st;
      }
      var on = detail.etat !== 'vide' && !!detail.texte;
      if (on) {
        write(link, detail.texte + (detail.action && !act ? ' · ' + detail.action : ''));
        if (detail.action) write(act, detail.action);
      } else { restore(link); restore(act); }
      var trigger = closest(link, attr('data-ouvrir') || SEL_OPEN);
      if (trigger) {
        trigger.setAttribute(ETAT, on ? detail.etat : 'vide');
        // nom accessible : un aria-label posé par le thème (« Calculer ma taille ») masquerait le texte affiché
        if (on && detail.aria && (trigger.hasAttribute('aria-label') || trigger.hasAttribute(ARIA0))) {
          if (!trigger.hasAttribute(ARIA0)) trigger.setAttribute(ARIA0, trigger.getAttribute('aria-label'));
          trigger.setAttribute('aria-label', detail.aria);
        } else if (!on && trigger.hasAttribute(ARIA0)) {
          trigger.setAttribute('aria-label', trigger.getAttribute(ARIA0));
          trigger.removeAttribute(ARIA0);
        }
      }
    } catch (e) {}
  }
  function publishAdvice(ctx, adv, applied) {
    if (!adv) return publish({ etat: 'vide', texte: '', action: '', aria: '', tailles: null, applique: false, raison: '' });
    var s = adv.shown, set = adv.kind === 'set';
    var etat = adv.status === 'taille' || adv.status === 'epuisee' || adv.status === 'aucune' ? adv.status : 'non-conseillee';
    var key = etat === 'taille' ? (set ? 'card_size_set' : 'card_size_single')
      : etat === 'epuisee' ? 'card_soldout' : etat === 'aucune' ? 'card_out' : 'card_not_advised';
    var texte = text(ctx.lang, key, { top: s.top, bottom: s.bottom, size: set ? s.top + ' / ' + s.bottom : s.single });
    var action = text(ctx.lang, 'card_edit');
    publish({
      etat: etat, texte: texte, action: action, aria: texte + '. ' + action,
      tailles: adv.level === 'ok' ? { haut: s.top || null, bas: s.bottom || null, unique: s.single || null } : null,
      applique: !!applied, raison: adv.status
    });
  }
  function fromMemory(ctx) {
    var m = memory.load();
    return m ? advise(m.answers, m.pref, ctx) : null;
  }

  // ---------- application + vérification ----------
  function check(t) {
    var r = t.axis.radios[t.axis.sizes.indexOf(t.size)]; // la valeur réelle du bouton peut différer de la taille (« The » = L)
    if (!r || r.checked) return;
    var label = null;
    if (r.id) label = d.querySelector('label[for="' + (w.CSS && w.CSS.escape ? w.CSS.escape(r.id) : r.id) + '"]');
    var hadFocus = d.activeElement === r;
    try { r.focus({ preventScroll: true }); } catch (e) {} // sinon le clic sur le label fait défiler la page jusqu'au radio
    (label || r).click(); // vrai clic sur .nk-swatch : le thème, le garde-fou et bundle-offers.js le voient
    if (!hadFocus) try { r.blur(); } catch (e2) {}
  }
  function checkedIn(fs) { return fs.querySelector('input[type="radio"]:checked'); }
  function legends(targets) { // variante déjà courante : le thème n'émet rien et le libellé vidé par le garde-fou resterait vide
    targets.forEach(function (t) {
      var r = checkedIn(t.axis.fs), vov = t.axis.fs.querySelector('.nk-opt__value');
      if (r && vov && String(vov.textContent || '').trim() !== r.value) vov.textContent = r.value;
    });
  }
  function verify(targets, variant, unit, n) {
    return pause(DELAYS[n]).then(function () {
      var ctx = context();
      var okSwatches = targets.every(function (t) { var r = checkedIn(t.axis.fs); return !!r && sizeOn(t.axis, r.value) === t.size; });
      var okVariant = unit || !!(ctx && ctx.idInput && String(ctx.idInput.value) === String(variant.id));
      var okGate = !d.querySelector(fiche().garde);
      if (okSwatches && okVariant && okGate) { legends(targets); return { ok: true, reason: null, variantId: variant ? variant.id : null }; }
      if (n === 2 && okSwatches) targets.forEach(function (t) { // rattrapage unique : le thème recalcule, le garde-fou compte
        checkedIn(t.axis.fs).dispatchEvent(new w.Event('change', { bubbles: true }));
      });
      if (n < DELAYS.length - 1) return verify(targets, variant, unit, n + 1);
      return fail(!okSwatches ? 'pastille-non-cochee' : !okVariant ? 'variante-non-suivie' : 'garde-fou-actif', variant ? variant.id : null);
    });
  }
  // Offre « à l'unité » des fiches à offres groupées : la taille existe-t-elle dans l'unité active ?
  function unitHasSize(bundle, size) {
    try {
      var units = JSON.parse(bundle.getAttribute('data-units') || 'null');
      if (!units || !units.length) units = [{ variants: JSON.parse(bundle.getAttribute('data-solo-variants') || '[]') }];
      var a = fiche().offresUnite, on = bundle.querySelector('[' + a + '].is-on');
      var u = units[on ? (parseInt(on.getAttribute(a), 10) || 0) : 0];
      return arr(u && u.variants).some(function (v) { return norm(v.size) === size; });
    } catch (e) { return false; }
  }
  // Une taille cochée compte comme un choix si le garde-fou (ou bundle-offers.js) a déjà retiré ou posé les défauts.
  function alreadyChosen(ctx) {
    if (state.userChose) return true;
    if (!guardReady() && !(ctx.bundle && fiche().offresTaille)) return false;
    return ctx.axes.some(function (a) { return a.visible && !!checkedIn(a.fs); });
  }
  // Page d'atterrissage : régler un champ comme le ferait la cliente. Liste : valeur puis « input » et « change »
  // (le script de la page recalcule la variante). Radio : vrai clic (coche + « change »), sans effet si désactivé.
  function setField(t) {
    var input = t.axis.radios[t.axis.sizes.indexOf(t.size)]; // la valeur réelle peut différer de la taille (« The » = L)
    if (!input) return;
    if (!t.axis.select) { if (!input.checked) input.click(); return; }
    t.axis.select.value = input.value;
    t.axis.select.dispatchEvent(new w.Event('input', { bubbles: true }));
    t.axis.select.dispatchEvent(new w.Event('change', { bubbles: true }));
  }
  function verifyLanding(want, variant, n) {
    return pause(DELAYS[n]).then(function () {
      var ctx = context(), axes = ctx ? ctx.axes : [];
      var okFields = want.every(function (t) {
        var a = axes.filter(function (x) { return x.position === t.position; })[0];
        return !!a && sizeOn(a, valueOf(a)) === t.size;
      });
      var okVariant = !!(ctx && ctx.idInput && String(ctx.idInput.value) === String(variant.id));
      if (okFields && okVariant) return { ok: true, reason: null, variantId: variant.id };
      if (n === 2 && okFields) axes.forEach(function (a) { // rattrapage unique : la page recalcule sa variante
        var on = a.select || a.radios.filter(function (r) { return r.checked; })[0];
        if (on) on.dispatchEvent(new w.Event('change', { bubbles: true }));
      });
      if (n < DELAYS.length - 1) return verifyLanding(want, variant, n + 1);
      return fail(!okFields ? 'champ-non-regle' : 'variante-non-suivie', variant.id);
    });
  }
  function applyLanding(sizes, origin) {
    // le script de la page pose ses écouteurs à DOMContentLoaded : avant, un « change » ne recalcule rien
    return waitFor(function () { return d.readyState !== 'loading'; }, 80, 100).then(function () {
      var ctx = context();
      if (!ctx || !ctx.inScope) return fail('pas-de-selecteur');
      var targets = targetsFor(ctx, sizes);
      if (targets.some(function (t) { return !RANK[t.size]; })) return fail('taille-manquante');
      // contrôle AVANT : aucun champ n'est touché si la variante n'existe pas ou est épuisée
      var variant = pickVariant(ctx, targets);
      if (!variant) return fail('inexistante');
      if (!variant.available) return fail('epuisee', variant.id);
      if (origin === 'memoire' && (state.userChose || ctx.axes.some(function (a) { return valueOf(a) !== ''; }))) return fail('deja-choisi');
      targets.forEach(setField); // haut puis bas
      return verifyLanding(targets.map(function (t) { return { position: t.axis.position, size: t.size }; }), variant, 0);
    });
  }
  function applyNow(sizes, origin) {
    if (landing()) return applyLanding(sizes, origin);
    var ready = function () { var c = context(); return !!(c && c.picker && c.picker.product); };
    // tant que le thème n'a pas chargé ses variantes, cocher une pastille ne change pas la variante du formulaire
    return waitFor(ready, 80, 100).then(function (isReady) {
      var ctx = context();
      if (!ctx || !ctx.inScope || !ctx.picker) return fail('pas-de-selecteur');
      if (!isReady) return fail('selecteur-pas-pret');
      var targets = targetsFor(ctx, sizes);
      if (targets.some(function (t) { return !RANK[t.size]; })) return fail('taille-manquante');
      var unit = !!ctx.bundle && targets.some(function (t) { return !t.axis.visible; });
      var todo = targets.filter(function (t) { return t.axis.visible; });
      var variant = null;
      if (!unit) {
        // contrôle AVANT : aucun clic si la variante n'existe pas (le thème en choisirait une autre en silence) ou est épuisée
        variant = pickVariant(ctx, targets);
        if (!variant) return fail('inexistante');
        if (!variant.available) return fail('epuisee', variant.id);
      } else if (!todo.length || !unitHasSize(ctx.bundle, todo[0].size)) return fail('taille-absente-unite');
      if (origin === 'memoire' && alreadyChosen(ctx)) return fail('deja-choisi');
      todo.forEach(check); // haut puis bas
      return verify(todo, variant, unit, 0);
    });
  }
  function apply(sizes, origin) {
    origin = origin || 'bouton';
    var p;
    try { p = applyNow(sizes || {}, origin); } catch (e) { p = Promise.resolve(fail('erreur')); }
    return p.then(null, function () { return fail('erreur'); }).then(function (res) {
      try {
        if (res.ok) {
          state.sizes = sizes; state.applied = true;
          if (origin === 'bouton') state.userChose = false;
        }
        track('apply', { ok: res.ok, reason: res.reason || '', origin: origin });
        if (origin !== 'memoire') { var ctx = context(); if (ctx) publishAdvice(ctx, fromMemory(ctx), res.ok); }
      } catch (e) {}
      return res;
    });
  }

  // ---------- bouton panier en attente de taille ----------
  // Conseil non applicable (non conseillée, aucune, épuisée) : rien n'est coché, et une sélection par défaut ne doit pas
  // passer pour un choix. Le garde-fou du thème le fait déjà au chargement ; on ne complète que là où il n'a pas joué
  // (légende non reconnue) ou quand les tailles en place ont été posées par le calculateur. Jamais sur une fiche à offres
  // groupées : bundle-offers.js y pose une taille d'office, choix voulu.
  function buttons(on) {
    var F = fiche();
    arr(d.querySelectorAll(F.atc)).forEach(function (b) {
      b.classList.toggle(F.attente, on);
      if (on) b.setAttribute(HOLD, '1'); else b.removeAttribute(HOLD);
    });
  }
  function hold() {
    var ctx = context();
    if (!ctx || !ctx.inScope || ctx.landing || (ctx.bundle && fiche().offresTaille) || state.userChose) return false; // landing : ses champs partent vides, son formulaire se garde seul
    if (guardReady() && !state.applied) return false;
    if (!variants(ctx).some(function (v) { return v.available; })) return false; // produit épuisé : bouton déjà inactif
    ctx.axes.forEach(function (a) {
      a.radios.forEach(function (r) { r.checked = false; });
      arr(a.fs.querySelectorAll('.nk-swatch.is-selected')).forEach(function (s) { s.classList.remove('is-selected'); });
      var vov = a.fs.querySelector('.nk-opt__value');
      if (vov) vov.textContent = '';
    });
    state.hold = {}; state.applied = false; state.sizes = null;
    buttons(true);
    return true;
  }
  function onPick(e) {
    if (!state.hold) return;
    var t = e.target, hit = e.type === 'click' ? closest(t, '.nk-swatch') : (t && t.type === 'radio' && t.checked ? t : null);
    var ctx = hit ? context() : null;
    if (!ctx) return;
    ctx.axes.forEach(function (a) { if (a.fs.contains(hit)) state.hold[a.position] = true; });
    var done = ctx.axes.every(function (a) { return !a.visible || state.hold[a.position]; });
    if (done) state.hold = null;
    buttons(!done); // le garde-fou du thème retire sa classe dès le premier clic : on la repose tant qu'il manque une taille
  }
  function blockClick(e) {
    var b = closest(e.target, fiche().atc);
    if (!b || !b.hasAttribute(HOLD)) return;
    e.preventDefault(); e.stopImmediatePropagation();
    var ctx = context();
    try { if (ctx && ctx.axes[0]) ctx.axes[0].fs.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e2) {}
  }
  function blockSubmit(e) {
    if (!d.querySelector('button[' + HOLD + ']')) return;
    e.preventDefault(); e.stopPropagation();
  }

  // ---------- pré-cochage au chargement ----------
  // Page chargée, variantes du thème, garde-fou passé (et bundle-offers.js sur ses fiches) : 6 s au plus.
  // Thème NAKED dont le garde-fou ne pose pas le signal window.__nkSizeSets (version d'avant le 02/10/2026, thèmes de
  // test) : le garde-fou s'exécute à DOMContentLoaded, il a donc joué une fois la page chargée ; on n'attend pas 6 s,
  // seulement 1 s après le chargement complet et les variantes.
  function whenReady(ctx) {
    if (ctx.landing) return waitFor(function () { return d.readyState === 'complete'; }, 60, 100);
    var late = 0;
    return waitFor(function () {
      var c = context();
      if (d.readyState !== 'complete' || !c || !c.picker || !c.picker.product) return false;
      if (!guardReady() && (fiche().gardePret || ++late <= 10)) return false;
      var pret = fiche().offresPret;
      return !c.bundle || (pret ? !!d.querySelector(pret) : (c.picker.dataset && c.picker.dataset.njbTag === '1'));
    }, 60, 100);
  }
  // Page d'atterrissage sans déclencheur : un bouton par formulaire, devant l'élément désigné (boutonAvant).
  function triggers(ctx) {
    var L = ctx.landing;
    if (!L || !L.boutonAvant || (L.ouvrir && d.querySelector(L.ouvrir))) return;
    var label = text(ctx.lang, 'dialog_title');
    if (!label) return; // textes non chargés : la popup n'en aurait pas non plus
    landingForms(L).forEach(function (form) {
      var before = form.querySelector(L.boutonAvant);
      if (!before || !before.parentNode || form.querySelector('[' + TRIGGER + ']')) return;
      var b = d.createElement('button');
      b.setAttribute('type', 'button');
      b.setAttribute(TRIGGER, '');
      b.setAttribute('aria-haspopup', 'dialog');
      if (L.boutonClasse) b.setAttribute('class', L.boutonClasse);
      b.textContent = label;
      before.parentNode.insertBefore(b, before);
    });
  }
  function precheck() {
    var ctx = context();
    if (!ctx || !ctx.inScope) return Promise.resolve(fail('hors-champ'));
    triggers(ctx);
    var adv = fromMemory(ctx);
    publishAdvice(ctx, adv, false); // tout de suite, sans attendre le chargement complet
    if (!adv) return Promise.resolve(fail('pas-de-memoire'));
    return whenReady(ctx).then(function () {
      if (adv.status !== 'taille') { hold(); return fail(adv.status); }
      if (ctx.landing && !ctx.landing.memoire) return fail('memoire-non-appliquee');
      return apply(adv.sizes, 'memoire').then(function (res) {
        var c = context();
        if (c) publishAdvice(c, adv, res.ok);
        return res;
      });
    }).then(function (res) {
      track('precheck', { done: res.ok, reason: res.reason || '' });
      return res;
    }, function () { return fail('erreur'); });
  }
  function refresh() {
    var ctx = context();
    if (!ctx || !ctx.inScope) return null;
    var adv = fromMemory(ctx);
    if (!adv) state.sizes = null; // mémoire effacée : un changement d'offre ne recoche plus l'ancienne taille
    publishAdvice(ctx, adv, false);
    if (adv && adv.status !== 'taille') hold();
    return adv;
  }

  // ---------- ouverture ----------
  function loadPopup(ctx) {
    if (w.NakedSizePopup) return Promise.resolve();
    if (state.popup) return state.popup;
    state.popup = new Promise(function (resolve, reject) {
      var css = ctx.root.getAttribute('data-popup-css'), js = ctx.root.getAttribute('data-popup-js');
      if (!js) return reject(new Error('popup'));
      if (css) { var l = d.createElement('link'); l.setAttribute('rel', 'stylesheet'); l.setAttribute('href', css); d.head.appendChild(l); }
      var brandCss = ctx.root.getAttribute('data-marque-css'); // après la feuille de la popup : ses variables priment
      if (brandCss) { var l2 = d.createElement('link'); l2.setAttribute('rel', 'stylesheet'); l2.setAttribute('href', brandCss); d.head.appendChild(l2); }
      var s = d.createElement('script');
      s.setAttribute('src', js);
      s.onload = function () { resolve(); };
      s.onerror = function () { reject(new Error('popup')); };
      w.setTimeout(function () { reject(new Error('popup')); }, 4000);
      d.head.appendChild(s);
    });
    state.popup.then(null, function () { state.popup = null; }); // échec : un nouvel essai reste possible
    return state.popup;
  }
  function popupIsOpen() {
    var P = w.NakedSizePopup, o = P && P.isOpen;
    try { return typeof o === 'function' ? !!o.call(P) : !!o; } catch (e) { return false; }
  }
  // Habillage de marque de la popup (rien sans data-marque ni data-sceau : popup NAKED telle quelle).
  function dress(ctx) {
    try {
      var brand = ctx.root.getAttribute('data-marque'), seal = ctx.root.getAttribute('data-sceau');
      var dlg = (brand || seal) ? d.querySelector('dialog.nsz') : null;
      if (!dlg) return;
      if (brand) dlg.setAttribute('data-marque', brand);
      if (seal) {
        var lines = seal.split('|');
        arr(dlg.querySelectorAll('.nsz-proof__seal text')).forEach(function (t, i) { if (lines[i] != null) t.textContent = lines[i]; });
      }
    } catch (e) {}
  }
  function open(opts) {
    var ctx = context();
    if (!ctx || !ctx.inScope) return Promise.resolve(false);
    var now = Date.now();
    // la carte transmet déjà son clic au petit lien : le même geste arrive deux fois
    if (state.opening || now - state.openedAt < 400 || popupIsOpen()) return Promise.resolve(false);
    state.opening = true; state.openedAt = now;
    var source = (opts && opts.source) || 'api';
    return loadPopup(ctx).then(function () {
      var P = w.NakedSizePopup, c = context() || ctx;
      if (!P || typeof P.open !== 'function') throw new Error('popup');
      P.open({ source: source, country: c.country, lang: c.lang, system: c.system, product: c.product, memory: memory.load() });
      dress(c);
      state.opening = false; state.openedAt = Date.now();
      track('open', { source: source });
      return true;
    }).then(null, function () {
      state.opening = false;
      track('error', { where: 'open' });
      try { var guide = ctx.root.getAttribute('data-guide'); if (guide) w.location.href = guide; } catch (e) {}
      return false;
    });
  }

  // ---------- écouteurs (délégués, posés une fois) ----------
  function inScopePage() { var c = context(); return !!(c && c.inScope); }
  // En phase de capture : sur une fiche v2, #naked-size-button n'existe pas et la carte (nkd-size-ui) transmet son clic
  // au petit lien .naked-size-inline-trigger, qui n'est pas rendu sur tous les gabarits. Le cœur prend donc le clic de
  // la carte lui-même, avant tout autre écouteur ; le clic transmis ensuite par la carte est absorbé par open() (400 ms).
  // La propagation n'est pas arrêtée : les autres écouteurs de la page (mesure, tests A/B) voient toujours le clic.
  d.addEventListener('click', safe(function onOpen(e) {
    var t = closest(e.target, openSel());
    if (!t || !inScopePage()) return; // page sans calculateur v2 : on ne touche à rien
    e.preventDefault();
    var L = landing();
    if (L) {
      // déclencheur de la page (ancien guide, tableau de secours) : son propre écouteur ne doit pas ouvrir autre chose
      if (!t.hasAttribute(TRIGGER)) e.stopImmediatePropagation();
      var forms = landingForms(L), own = closest(t, 'form');
      state.form = own ? forms.indexOf(own) : -1; // la taille sera réglée et vérifiée dans le formulaire du déclencheur
      open({ source: 'landing' });
      return;
    }
    open({ source: t.hasAttribute(fiche().carte) ? 'carte' : 'lien' });
  }), true);
  d.addEventListener('change', safe(function onField(e) { // landing : un champ réglé à la main (les « change » du calculateur ont isTrusted à false)
    var L = e.isTrusted ? landing() : null, form = L ? closest(e.target, 'form') : null;
    if (!form || !fieldsOf(form, L).some(function (f) { return f.el === e.target || (f.el.contains && f.el.contains(e.target)); })) return;
    if (state.applied && !state.userChose) track('size_override', {});
    state.userChose = true;
  }), true);
  d.addEventListener('pointerdown', safe(function onWarm(e) {
    var c = closest(e.target, openSel()) ? context() : null;
    if (c && c.inScope) loadPopup(c).then(null, function () {});
  }));
  d.addEventListener('click', safe(function onUser(e) { // « à la main » = clic réel ; les clics du calculateur ont isTrusted à false
    if (!e.isTrusted || !closest(e.target, '.nk-swatch') || !closest(e.target, '.nk-pdp-info')) return;
    if (state.applied && !state.userChose) track('size_override', {});
    state.userChose = true;
  }), true);
  d.addEventListener('click', safe(onPick));
  d.addEventListener('change', safe(onPick));
  d.addEventListener('click', safe(function onOffer(e) { // changement d'offre groupée : bundle-offers.js reprend la main, on recoche après lui
    if (!closest(e.target, fiche().offresChoix) || !state.sizes || state.userChose) return;
    var sizes = state.sizes;
    w.setTimeout(safe(function () { apply(sizes, 'offre'); }), 80);
  }));
  if (w.addEventListener) w.addEventListener('click', safe(blockClick), true);
  d.addEventListener('submit', safe(blockSubmit), true);

  var api = {
    __core: true,
    VERSION: '2.0.0',
    context: context,
    recommend: function (answers, pref) { try { return advise(answers, pref); } catch (e) { return null; } },
    variantFor: function (sizes) { try { return variantFor(sizes); } catch (e) { return null; } },
    apply: apply,
    memory: memory,
    refresh: function () { try { return refresh(); } catch (e) { return null; } },
    precheck: precheck,
    open: function (opts) { try { return open(opts); } catch (e) { return Promise.resolve(false); } },
    track: track,
    cardState: null
  };
  w.NakedSize = api;

  try { precheck().then(null, function () {}); } catch (e) {}
})(typeof window !== 'undefined' ? window : null);
